import { call, put, takeLatest } from "redux-saga/effects";

import { ActionWithPayload, EvalRunListFilter, ResponseError, WithCallback, WithLoaderFlag } from "@shared/interfaces";
import { showHttpErrorNotification, downloadFile } from "@shared/utils";
import { showNotification, startLoading, stopLoading } from "@shared/store/actions";
import { EvalCategoryModel, EvalRunDetailModel } from "@shared/models";

import {
  deleteEvalRun,
  downloadEvalTemplate,
  getEvalCategories,
  getEvalRun,
  getEvalRuns,
  runEvalFromCsv,
  setEvalRunsFilter,
} from "./actions";
import { EvalCategoriesResponse, EvalRunListResponse, RunEvalFromCsvDto } from "../interface";
import api from "../api";

// `isLoadingBlocked` marks a silent poll for running runs: no loader and no error toast.
function* getEvalRunsSaga({ payload }: ActionWithPayload<WithLoaderFlag<EvalRunListFilter>>) {
  const { isLoadingBlocked, ...filter } = payload;
  try {
    if (!isLoadingBlocked) yield put(startLoading());
    const response: EvalRunListResponse = yield call(api.getEvalRuns, filter);
    yield put(
      getEvalRuns.success({
        rows: response.results,
        count: response.total,
        clear: !filter.page,
      }),
    );
  } catch (error) {
    yield put(getEvalRuns.failure(error as Error));
    if (!isLoadingBlocked) yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    if (!isLoadingBlocked) yield put(stopLoading());
  }
}

function* getEvalRunSaga({ payload }: ActionWithPayload<WithLoaderFlag<{ uuid: string }>>) {
  const { uuid, isLoadingBlocked } = payload;
  try {
    if (!isLoadingBlocked) yield put(startLoading());
    const response: EvalRunDetailModel = yield call(api.getEvalRun, uuid);
    yield put(getEvalRun.success(response));
  } catch (error) {
    yield put(getEvalRun.failure(error as Error));
    if (!isLoadingBlocked) yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    if (!isLoadingBlocked) yield put(stopLoading());
  }
}

function* deleteEvalRunSaga({ payload }: ActionWithPayload<string>) {
  try {
    yield put(startLoading());
    yield call(api.deleteEvalRun, payload);
    yield put(deleteEvalRun.success(payload));
  } catch (error) {
    yield put(deleteEvalRun.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* runEvalFromCsvSaga({ payload }: ActionWithPayload<WithCallback<RunEvalFromCsvDto>>) {
  const { cb, ...rest } = payload;
  try {
    const response: EvalRunDetailModel = yield call(api.runEvalFromCsv, rest);
    yield put(runEvalFromCsv.success(response));
    yield put(
      showNotification({
        message: `Evaluation started for ${response.total_cases} case${response.total_cases === 1 ? "" : "s"}.`,
        appearance: "success",
      }),
    );
    // Resets the filter to its first page, which re-requests the list.
    yield put(setEvalRunsFilter(null));
    cb?.();
  } catch (error) {
    yield put(runEvalFromCsv.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError, "Evaluation failed. Please try again."));
  }
}

function* getEvalCategoriesSaga() {
  try {
    const response: EvalCategoriesResponse = yield call(api.getEvalCategories);
    yield put(getEvalCategories.success(response.categories as EvalCategoryModel[]));
  } catch (error) {
    yield put(getEvalCategories.failure(error as Error));
  }
}

function* downloadEvalTemplateSaga() {
  try {
    yield put(startLoading());
    const response: string = yield call(api.getEvalTemplate);
    downloadFile(response, "eval-template.csv");
    yield put(downloadEvalTemplate.success());
  } catch (error) {
    yield put(downloadEvalTemplate.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError, "Can't download the template. Please try again."));
  } finally {
    yield put(stopLoading());
  }
}

function* evaluationSaga() {
  yield takeLatest(getEvalRuns.request, getEvalRunsSaga);
  yield takeLatest(getEvalRun.request, getEvalRunSaga);
  yield takeLatest(deleteEvalRun.request, deleteEvalRunSaga);
  yield takeLatest(runEvalFromCsv.request, runEvalFromCsvSaga);
  yield takeLatest(getEvalCategories.request, getEvalCategoriesSaga);
  yield takeLatest(downloadEvalTemplate.request, downloadEvalTemplateSaga);
}

export default evaluationSaga;
